import {
  ProjectsService,
  TemplatesService,
  OpenAPI,
  ApiError,
  type AIChatRequest,
  type AiChatAttachmentInput,
  type AiTemplateChatRequest,
  type AiMessage,
  type AiMessageToolUsage,
  type AiStep,
  type AiToolCall,
  type AiWriteRequest,
  AI_TOOL_METADATA,
  AI_TEMPLATE_TOOL_METADATA,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { aiErrorKind, type AiErrorKind } from '@/composables/aiChatErrors';
import { useActivityProjectStore } from '@/store/activityProjectStore';
import { useActivityTemplateStore } from '@/store/activityTemplateStore';
import { useAiLiveStore } from '@/store/aiLiveStore';
import { useFeatureFlagStore } from '@/store/featureFlagStore';
import { useHistoryStore } from '@/store/historyStore';
import { useMilestoneTemplateStore } from '@/store/milestoneTemplateStore';
import { useProjectStore } from '@/store/projectStore/projectStore';
import { useTemplateStore } from '@/store/templateStore/templateStore';
import { getLocale } from '@/utils/i18n/vueI18n';

/**
 * The AI assistant's chats and their runs (WI #5766). The run of an answer
 * belongs to the page, not to the chat panel: closing the dock or switching
 * between map, list and kanban keeps it attached, so its steps keep arriving,
 * the Sparkles button shows it running, and the project/template data is
 * refreshed when it ends. One chat per project/template; `useAIAssistant`
 * binds a component to it.
 */

export type AIAssistantMode = 'project' | 'template';

export interface ToolUsed {
  name: string;
  status: 'success' | 'error';
  iteration?: number;
}

/** One tool call of an answer, merged from `tool_start` / `tool_end`. */
export interface ToolCallState {
  toolCallId: string;
  name: string;
  iteration?: number;
  status: AiToolCall['status'];
  /** Write calls: how many entities the call works on. */
  itemCount?: number;
  error?: string;
}

export interface CodeInterpreterImage {
  data: string;
  mimeType: string;
  attachmentId?: string;
}

export interface CodeInterpreterBlock {
  code: string;
  logs?: string;
  images?: CodeInterpreterImage[];
  isExecuting?: boolean;
}

export interface MessageAttachment {
  id: string;
  type: string;
  mimeType: string;
  // `input` for user-uploaded files, `output` for assistant-generated ones
  // (e.g. code-interpreter images). Defaults to `output` on legacy rows.
  source?: 'input' | 'output';
  // Populated only for user-uploaded (input) attachments — used to render
  // the filename + size in the chat bubble. Bytes aren't persisted on our
  // side so there's no download link.
  filename?: string;
  size?: number;
  // Set when the user uploaded this file into a skill's named slot. Drives
  // the runtime "this slot is already filled in the conversation"
  // indicator so we don't re-prompt for the same attachment on every
  // follow-up turn.
  slotId?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  toolsUsed?: ToolUsed[];
  /** The answer's tool calls and the entities they changed (WI #5766). */
  toolCalls?: ToolCallState[];
  steps?: AiStep[];
  /** The model that wrote the answer, as the realm names it. */
  model?: string;
  iterations?: number;
  durationMs?: number;
  id?: string;
  createdAt?: string;
  inputTokens?: number;
  outputTokens?: number;
  cachedTokens?: number;
  estimatedCost?: number | null;
  // Streaming-specific fields
  isStreaming?: boolean;
  reasoningText?: string;
  codeBlocks?: CodeInterpreterBlock[];
  // Persisted image attachments (loaded from history)
  attachments?: MessageAttachment[];
  // HTML rendering of the user's input (with styled @-mention chips) used
  // only for the chat bubble display. Set at send-time for user messages;
  // unavailable on messages loaded from history (bubble falls back to the
  // plain `content` in that case).
  displayHtml?: string;
  // Persisted lifecycle status: 'streaming' means the backend is still
  // generating this response; if we load the conversation while a prior
  // tab left one behind, we tail it via the message-stream endpoint.
  // 'stopped': the user stopped it (WI #5766).
  status?: AiMessage['status'];
  errorMessage?: string | null;
  // Slice A of WI #5386 — orchestrator's dispatched subagent runs, in the
  // order the parent agent called them. Populated from the live SSE stream
  // and from `metadata.subagent_runs` after reload.
  subagentRuns?: SubagentRunRecord[];
  /**
   * The answer's pauses for write access (WI #5768): the approval card at
   * each, the last one `pending` while the answer waits for the user.
   */
  writeRequests?: AiWriteRequest[];
}

/**
 * One subagent run dispatched by the parent assistant. Lives in
 * ChatMessage.subagentRuns. The shape mirrors the backend metadata
 * (`subagent_runs` in AiMessage.metadata) with one extra FE-only field:
 * `status: 'running'` for in-flight runs that haven't completed yet.
 */
export interface SubagentRunRecord {
  index: number;
  name: string;
  label: string;
  prompt: string;
  /** Parent iteration in which this subagent was dispatched. */
  iteration: number;
  resultText: string;
  /** Subagent's own internal iteration count. */
  iterations: number;
  toolsUsed: Array<{
    name: string;
    iteration: number;
    status: 'success' | 'error';
  }>;
  inputTokens?: number;
  outputTokens?: number;
  cachedTokens?: number;
  cost?: number;
  /**
   * `'running'` is FE-only while the run is in flight; `'stopped'`: the
   * user's stop ended it (WI #5766).
   */
  status: 'running' | 'success' | 'error' | 'truncated' | 'stopped';
  error?: string;
}

// Status types for streaming
export type StreamingStatusType =
  | 'idle'
  | 'thinking'
  | 'processing'
  | 'tool'
  | 'code_executing'
  | 'subagent';

export interface StreamingStatus {
  type: StreamingStatusType;
  iteration?: number;
  reasoningText?: string;
  /**
   * Live counters when one or more subagents are running. Set on
   * `subagent_dispatched` and decremented on `subagent_complete`. UI shows
   * "N subagents running" while non-zero.
   */
  subagentRunning?: number;
  subagentTotal?: number;
  /** Most recently dispatched subagent's label — shown alongside counters. */
  subagentLabel?: string;
}

/**
 * A message of the shown chat that got no answer (WI #5769, D10): the chat
 * says why inline, in German, and offers "Erneut senden" with the prompt
 * back in the input. An answer that already changed something leaves none —
 * sent again, it would make its changes twice.
 */
export interface AiChatFailure {
  kind: AiErrorKind;
  /**
   * The server never got the message, so nothing of it is stored: it left
   * the chat, which shows the error after its last message. Otherwise the
   * stored answer shows it.
   */
  unsent: boolean;
  /** The prompt as sent. */
  prompt: string;
  /** The input's document as sent, with its chips — opaque to the store. */
  draft?: unknown;
  /** The files sent with it, for an input that lost them meanwhile. */
  attachments?: AiChatAttachmentInput[];
  /** The input took the prompt back. */
  restored: boolean;
}

/** The chat of one project or template. */
export interface AiChat {
  mode: AIAssistantMode;
  contextId: string;
  messages: ChatMessage[];
  /** A run is attached to the shown conversation. */
  loading: boolean;
  loadingMessages: boolean;
  /** The last message got no answer (WI #5769). */
  failure: AiChatFailure | null;
  /** The stored conversation could not be loaded. */
  loadError: AiErrorKind | null;
  conversationId: string | null;
  // Skill that's sticky on the current conversation. Source of truth is the
  // server (`AiConversation.activeSkillId`). Hydrated on conversation load
  // and on send (optimistic update) so the banner / tool-lock UI reflects
  // state without a refetch.
  activeSkillId: string | null;
  // `ai_model.id` (UUID) sticky on the current conversation. Source of
  // truth is `AiConversation.aiModelId`; null means "use the realm's
  // capability default".
  selectedAiModelId: string | null;
  currentStatus: StreamingStatus;
  // Absolute wall-clock timestamp (ms) when the currently-active stream
  // began. For fresh sends this is `Date.now()` at send-time; for resumed
  // streams it's the assistant message's `createdAt` so the bottom-right
  // timer shows the *true* elapsed time instead of restarting at 0.
  streamStartTime: number | null;
  /** The user stopped the answer; the run finishes the entity being saved. */
  stopping: boolean;
  /** Runs the user left for another conversation that are still going. */
  backgroundRunCount: number;
  /** The stored conversation was loaded. */
  initialized: boolean;
  /**
   * The user allowed the assistant to change things in the shown chat (WI
   * #5768): its write calls run without asking until revoked.
   */
  writePermission: boolean;
  /**
   * The assistant offers its write tools to the user here — an edit role in
   * the project, owner or editor of the template; null until the backend
   * said. False: the input shows "Nur lesen".
   */
  canEdit: boolean | null;
  /** The `+` menu's switches, sent with each message. */
  webSearch: boolean;
  codeInterpreter: boolean;
}

type AiAssistantStoreState = {
  chats: Record<string, AiChat>;
};

// SSE Event types
interface SSEEvent {
  event: string;
  data: unknown;
}

/**
 * A stream that feeds a message: the live answer of a send, or the tail of
 * an answer that was running before a reload. Kept outside the store state —
 * it holds the fetch and the message it writes into.
 */
interface Run {
  key: string;
  abort: AbortController;
  /** The (reactive) message the stream writes into. */
  message: ChatMessage;
  /** The run's conversation, once the server named it. */
  conversationId?: string;
  /** The user stopped it; sent with the `session` event if it was early. */
  stopRequested: boolean;
  /** The server named the answer: the stream belongs to it. */
  sessionStarted?: boolean;
}

/** Per chat: the run attached to the shown conversation. */
const attachedRuns = new Map<string, Run>();
/**
 * Runs whose conversation the user left (switched or started a new chat):
 * they keep going to their end, so a stop the user pressed still reaches
 * the server and their changes still reach the page.
 */
const backgroundRuns = new Set<Run>();

const MESSAGE_LOAD_LIMIT = 100;

// Tools that modify data — the fallback for answers without steps.
const PROJECT_DATA_MODIFYING_TOOLS = AI_TOOL_METADATA.filter(
  (tool) => tool.category === 'write',
).map((tool) => tool.name);

const TEMPLATE_DATA_MODIFYING_TOOLS = AI_TEMPLATE_TOOL_METADATA.filter(
  (tool) => tool.category === 'write',
).map((tool) => tool.name);

export function chatKey(mode: AIAssistantMode, contextId: string): string {
  return `${mode}:${contextId}`;
}

function conversationStorageKey(chat: AiChat): string {
  return `ai-conversation-${chat.mode}-${chat.contextId}`;
}

function newChat(mode: AIAssistantMode, contextId: string): AiChat {
  return {
    mode,
    contextId,
    messages: [],
    loading: false,
    loadingMessages: false,
    failure: null,
    loadError: null,
    conversationId: null,
    activeSkillId: null,
    selectedAiModelId: null,
    currentStatus: { type: 'idle' },
    streamStartTime: null,
    stopping: false,
    backgroundRunCount: 0,
    initialized: false,
    writePermission: false,
    canEdit: null,
    webSearch: false,
    codeInterpreter: false,
  };
}

function runStorageKey(mode: AIAssistantMode, contextId: string): string {
  return `ai-run-${mode}-${contextId}`;
}

/**
 * Marks that the shown conversation's answer is running, so a page loaded
 * meanwhile (a reload, another tab) rejoins it with the dock closed.
 */
function markRunning(chat: AiChat, running: boolean) {
  try {
    const key = runStorageKey(chat.mode, chat.contextId);
    if (running) {
      localStorage.setItem(key, '1');
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Storage blocked: the answer is rejoined when the dock is opened.
  }
}

function markedRunning(mode: AIAssistantMode, contextId: string): boolean {
  try {
    return localStorage.getItem(runStorageKey(mode, contextId)) !== null;
  } catch {
    return false;
  }
}

function decisionStorageKey(mode: AIAssistantMode, contextId: string): string {
  return `ai-decision-${mode}-${contextId}`;
}

/**
 * Marks that the shown conversation's answer waits for the user's decision
 * on its write calls (WI #5768), so a page loaded meanwhile loads the chat
 * with the dock closed — for the Sparkles button's badge.
 */
function markDecision(chat: AiChat, waiting: boolean) {
  try {
    const key = decisionStorageKey(chat.mode, chat.contextId);
    if (waiting) {
      localStorage.setItem(key, '1');
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Storage blocked: the badge shows once the dock was opened.
  }
}

function markedDecision(mode: AIAssistantMode, contextId: string): boolean {
  try {
    return localStorage.getItem(decisionStorageKey(mode, contextId)) !== null;
  } catch {
    return false;
  }
}

let toolSelectionForgotten = false;

/**
 * The tool panel's per-browser selection is gone (WI #5768): the backend
 * picks the tools. Its stored keys are dropped once per page.
 */
function forgetToolSelection() {
  if (toolSelectionForgotten) {
    return;
  }
  toolSelectionForgotten = true;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('ai-tools-selection-')) {
        keys.push(key);
      }
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage blocked: nothing stored either.
  }
}

/** The answer of the chat that waits for the user's decision, if any. */
function waitingAnswer(chat: AiChat): ChatMessage | undefined {
  const last = chat.messages[chat.messages.length - 1];

  return last?.role === 'assistant' && last.status === 'awaiting_permission'
    ? last
    : undefined;
}

/**
 * Settles the pending pause of an answer, as the backend does: decided, or
 * dropped for a new message — then its held calls end unrun.
 */
function settleWriteRequest(
  msg: ChatMessage,
  status: Exclude<AiWriteRequest['status'], 'pending'>,
  scope?: AiWriteRequest['scope'],
) {
  for (const request of msg.writeRequests ?? []) {
    if (request.status === 'pending') {
      request.status = status;
      if (scope) {
        request.scope = scope;
      }
    }
  }
  if (status !== 'allowed') {
    for (const call of msg.toolCalls ?? []) {
      if (call.status === 'held') {
        call.status = 'stopped';
      }
    }
  }
}

/** Sets the conversation and remembers it for the next visit. */
function setConversationId(chat: AiChat, conversationId: string | null) {
  chat.conversationId = conversationId;
  try {
    if (conversationId) {
      localStorage.setItem(conversationStorageKey(chat), conversationId);
    } else {
      localStorage.removeItem(conversationStorageKey(chat));
    }
  } catch {
    // Storage blocked: the chat just starts empty next time.
  }
}

/**
 * The chat shows no stored conversation any more: the next message starts a
 * new one, which asks again before it changes anything (WI #5768).
 */
function resetConversation(chat: AiChat) {
  chat.messages = [];
  setConversationId(chat, null);
  chat.activeSkillId = null;
  chat.selectedAiModelId = null;
  chat.writePermission = false;
  markDecision(chat, false);
}

/**
 * Parse SSE events from a buffer
 */
export function parseSSEBuffer(buffer: string): {
  parsed: SSEEvent[];
  remaining: string;
} {
  const events: SSEEvent[] = [];
  const lines = buffer.split('\n');
  let currentEvent = '';
  let currentData = '';
  let remaining = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check if this might be an incomplete event at the end
    if (i === lines.length - 1 && line !== '') {
      // This line doesn't end with newline, keep it as remaining
      remaining = lines.slice(i).join('\n');
      break;
    }

    if (line.startsWith('event: ')) {
      currentEvent = line.slice(7);
    } else if (line.startsWith('data: ')) {
      currentData = line.slice(6);
    } else if (line === '' && currentEvent && currentData) {
      // End of event
      try {
        events.push({
          event: currentEvent,
          data: JSON.parse(currentData),
        });
      } catch {
        // Keep raw data if JSON parse fails
        events.push({
          event: currentEvent,
          data: currentData,
        });
      }
      currentEvent = '';
      currentData = '';
    }
  }

  // If we have a partial event, keep it in remaining
  if (currentEvent || currentData) {
    const lastEventIndex = buffer.lastIndexOf('event: ');
    if (lastEventIndex !== -1 && !buffer.endsWith('\n\n')) {
      remaining = buffer.slice(lastEventIndex);
    }
  }

  return { parsed: events, remaining };
}

/**
 * Coerce the backend `subagent_runs` array (snake_case JSONB blob from
 * AiMessage.metadata) into our camelCase SubagentRunRecord shape. Used both
 * for messages loaded from history and for tail-on-reload attach events.
 * Returns undefined when the input is empty/missing so we don't leak empty
 * arrays into the chat UI.
 */
function parseSubagentRunsFromMetadata(
  raw: unknown,
): SubagentRunRecord[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }

  // Renumber from array position rather than trusting the persisted
  // `index` field. Records from before the synchronous-counter fix could
  // share indices (the parallel-dispatch race assigned them all to 0);
  // re-keying by position guarantees `find(r => r.index === ...)` lookups
  // do not silently route to a stale duplicate.
  return raw.map((r, idx) => {
    const run = r as Record<string, unknown>;

    return {
      index: idx,
      name: String(run.name ?? ''),
      label: String(run.label ?? run.name ?? ''),
      prompt: String(run.prompt ?? ''),
      iteration: Number(run.iteration ?? 0),
      resultText: String(run.result_text ?? ''),
      iterations: Number(run.iterations ?? 0),
      toolsUsed: Array.isArray(run.tools_used)
        ? (run.tools_used as Array<Record<string, unknown>>).map((t) => ({
            name: String(t.name ?? ''),
            iteration: Number(t.iteration ?? 0),
            status:
              t.status === 'error' ? ('error' as const) : ('success' as const),
          }))
        : [],
      inputTokens:
        typeof run.input_tokens === 'number' ? run.input_tokens : undefined,
      outputTokens:
        typeof run.output_tokens === 'number' ? run.output_tokens : undefined,
      cachedTokens:
        typeof run.cached_tokens === 'number' ? run.cached_tokens : undefined,
      cost: typeof run.cost === 'number' ? run.cost : undefined,
      status:
        run.status === 'error'
          ? 'error'
          : run.status === 'truncated'
            ? 'truncated'
            : run.status === 'stopped'
              ? 'stopped'
              : 'success',
      error: typeof run.error === 'string' ? run.error : undefined,
    };
  });
}

function parseToolCalls(raw: unknown): ToolCallState[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }

  return (raw as AiToolCall[]).map((call) => ({
    toolCallId: call.tool_call_id,
    name: call.name,
    iteration: call.iteration,
    status: call.status,
    itemCount: call.item_count,
    error: call.error,
  }));
}

/** The answer's metadata as the message keeps it (history, attach, done). */
function applyMetadata(
  msg: ChatMessage,
  metadata: Record<string, unknown> | null | undefined,
) {
  const meta = (metadata ?? {}) as {
    iterations?: number;
    tools_used?: AiMessageToolUsage[];
    tool_calls?: unknown;
    steps?: AiStep[];
    subagent_runs?: unknown;
    input_tokens?: number;
    output_tokens?: number;
    cached_tokens?: number;
    model?: string;
    duration_ms?: number;
    write_requests?: AiWriteRequest[];
  };
  msg.iterations = meta.iterations;
  msg.inputTokens = meta.input_tokens;
  msg.outputTokens = meta.output_tokens;
  msg.cachedTokens = meta.cached_tokens;
  msg.model = meta.model;
  if (typeof meta.duration_ms === 'number') {
    msg.durationMs = meta.duration_ms;
  }
  msg.toolsUsed = meta.tools_used?.map((tool) => ({
    name: tool.name,
    status: tool.status,
    iteration: tool.iteration,
  }));
  msg.toolCalls = parseToolCalls(meta.tool_calls);
  msg.steps = Array.isArray(meta.steps) ? [...meta.steps] : undefined;
  msg.subagentRuns = parseSubagentRunsFromMetadata(meta.subagent_runs);
  msg.writeRequests = Array.isArray(meta.write_requests)
    ? meta.write_requests.map((request) => ({ ...request }))
    : undefined;
}

// Helper function to convert backend AiMessage to ChatMessage
function convertToInternalMessage(msg: AiMessage): ChatMessage {
  const message: ChatMessage = {
    role: msg.role,
    content: msg.content,
    id: msg.id,
    createdAt: msg.created_at,
    estimatedCost: msg.estimated_cost,
    attachments: msg.attachments?.map((att) => ({
      id: att.id,
      type: att.type,
      mimeType: att.mime_type,
      source: att.source,
      filename: att.filename,
      size: att.size,
      slotId: att.slot_id,
    })),
    displayHtml: msg.display_html ?? undefined,
    status: msg.status,
    errorMessage: msg.error_message ?? null,
    // A message loaded with status='streaming' was still being generated
    // on the backend at the time the conversation was fetched. Render it
    // as an in-progress bubble; a follow-up tail connection refreshes it.
    isStreaming: msg.status === 'streaming' ? true : undefined,
  };
  applyMetadata(message, msg.metadata as Record<string, unknown> | null);

  return message;
}

/**
 * Inserts a step by its position; a step the message holds is skipped.
 * True when the step is new.
 */
function addStep(msg: ChatMessage, step: AiStep): boolean {
  const steps = msg.steps ?? (msg.steps = []);
  if (steps.some((existing) => existing.seq === step.seq)) {
    return false;
  }
  const at = steps.findIndex((existing) => existing.seq > step.seq);
  if (at === -1) {
    steps.push(step);
  } else {
    steps.splice(at, 0, step);
  }

  return true;
}

/** Merges a `tool_start` / `tool_end` into the call it belongs to. */
function mergeToolCall(
  msg: ChatMessage,
  data: {
    name: string;
    tool_call_id?: string;
    item_count?: number;
    status?: ToolCallState['status'];
    error?: string;
  },
) {
  if (!data.tool_call_id) {
    return;
  }
  const calls = msg.toolCalls ?? (msg.toolCalls = []);
  let call = calls.find((c) => c.toolCallId === data.tool_call_id);
  if (!call) {
    call = {
      toolCallId: data.tool_call_id,
      name: data.name,
      status: 'running',
    };
    calls.push(call);
  }
  if (data.item_count !== undefined) {
    call.itemCount = data.item_count;
  }
  // A repeated `tool_start` (a reattach) never reopens an ended call.
  if (data.status) {
    call.status = data.status;
    call.error = data.error;
  }
}

/** True when the answer changed the project/template. */
function changedData(chat: AiChat, msg: ChatMessage): boolean {
  if (msg.steps) {
    return msg.steps.some((step) => step.status === 'success');
  }
  // Answers from before the steps (or a stream that lost them).
  const writeTools =
    chat.mode === 'template'
      ? TEMPLATE_DATA_MODIFYING_TOOLS
      : PROJECT_DATA_MODIFYING_TOOLS;

  return (msg.toolsUsed ?? []).some(
    (tool) => tool.status === 'success' && writeTools.includes(tool.name),
  );
}

/**
 * Reloads what an answer changed, once its run ended — done, stopped or
 * failed, live or reattached, also with the chat panel closed (WI #5766).
 * The socket echo of these changes is dropped as the user's own, so this is
 * how the page shows them.
 */
async function refreshAfterRun(chat: AiChat, msg: ChatMessage): Promise<void> {
  if (!changedData(chat, msg)) {
    return;
  }
  const contextId = chat.contextId;
  try {
    // A live refresh still in flight must not overwrite this reload with an
    // older answer of the API (WI #5767).
    await useAiLiveStore().whenSettled(chat.mode, contextId);
    if (chat.mode === 'template') {
      // Cached details first, as for projects: no render should see a
      // fresh template next to stale sidebar details (WI #5747).
      await Promise.all([
        useActivityTemplateStore().reloadCachedActivities(contextId),
        useMilestoneTemplateStore().reloadCachedMilestones(contextId),
      ]);
      await useTemplateStore().loadTemplate(contextId);
    } else {
      const projectStore = useProjectStore();
      await useActivityProjectStore().reloadCachedActivities(contextId);
      await projectStore.loadProject(contextId);
      // Members live in a separate store record that loadProject does not
      // touch, so refresh it only when the answer added a member.
      if (
        msg.steps?.some(
          (step) => step.status === 'success' && step.entity_type === 'member',
        )
      ) {
        await projectStore.loadMembers(contextId);
      }
      await useHistoryStore().fetchLastHistoryOperation(contextId);
    }
  } catch (err) {
    console.error('Failed to reload data after the AI answer:', err);
  }
}

/** A chat request the server refused before its stream started. */
class AiRequestError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/**
 * A message the server never got (WI #5769): it leaves the chat, as nothing
 * of it is stored, and the chat shows why after its last message.
 */
function dropUnsent(chat: AiChat, msg: ChatMessage, failure: AiChatFailure) {
  const index = chat.messages.indexOf(msg);
  if (index > 0) {
    chat.messages.splice(index - 1, 2);
  }
  // The chat was deleted elsewhere: the message goes again in a new one.
  if (failure.kind === 'notFound') {
    resetConversation(chat);
  }
  chat.failure = failure;
}

async function authToken(): Promise<string> {
  if (typeof OpenAPI.TOKEN === 'function') {
    return OpenAPI.TOKEN({} as never);
  }

  return OpenAPI.TOKEN ?? '';
}

/** Reads an answer's SSE stream into its message until it ends. */
async function readStream(
  chat: AiChat,
  run: Run,
  body: ReadableStream<Uint8Array>,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const { parsed, remaining } = parseSSEBuffer(buffer);
    buffer = remaining;

    for (const sseEvent of parsed) {
      handleSSEEvent(chat, run, sseEvent);
    }
  }
}

/**
 * Handle individual SSE events. A run the user left (another
 * conversation shown) still updates its message, but no longer the
 * chat's status.
 */
function handleSSEEvent(chat: AiChat, run: Run, event: SSEEvent) {
  const msg = run.message;
  const attached = attachedRuns.get(run.key) === run;

  switch (event.event) {
    case 'session': {
      const data = event.data as {
        conversationId: string;
        assistantMessageId?: string;
      };
      run.conversationId = data.conversationId;
      run.sessionStarted = true;
      if (attached) {
        setConversationId(chat, data.conversationId);
      }
      // Remember the server-assigned id on the placeholder so a
      // mid-stream reload can reattach via the same message row.
      if (data.assistantMessageId) {
        msg.id = data.assistantMessageId;
        msg.status = 'streaming';
        if (attached) {
          markRunning(chat, true);
        }
        // The user pressed Stop before the server named the answer.
        if (run.stopRequested) {
          void requestStop(chat, run);
        }
      }
      break;
    }

    case 'attach': {
      // Initial snapshot from the tail endpoint — overwrite local state
      // with authoritative DB content before live events start flowing.
      const data = event.data as {
        content: string;
        status: ChatMessage['status'];
        errorMessage?: string | null;
        metadata?: Record<string, unknown> | null;
        attachments?: Array<{
          id: string;
          type: string;
          mime_type: string;
        }>;
      };
      // Steps saved while the connection was down come only with the
      // snapshot: they show now, not at the end; a step shown before does
      // not show again (WI #5767).
      const shown = new Set((msg.steps ?? []).map(({ seq }) => seq));
      msg.content = data.content || '';
      msg.status = data.status;
      msg.errorMessage = data.errorMessage ?? null;
      applyMetadata(msg, data.metadata);
      for (const step of msg.steps ?? []) {
        if (!shown.has(step.seq)) {
          useAiLiveStore().stepSaved(chat.mode, chat.contextId, step);
        }
      }
      if (data.attachments?.length) {
        msg.attachments = data.attachments.map((a) => ({
          id: a.id,
          type: a.type,
          mimeType: a.mime_type,
        }));
      }
      break;
    }

    case 'status': {
      const data = event.data as {
        type: 'thinking' | 'processing' | 'code_executing';
        iteration?: number;
      };
      if (data.type === 'code_executing') {
        // Mark current code block as executing
        if (msg.codeBlocks && msg.codeBlocks.length > 0) {
          msg.codeBlocks[msg.codeBlocks.length - 1].isExecuting = true;
        }
      }
      if (attached) {
        chat.currentStatus =
          data.type === 'code_executing'
            ? { type: 'code_executing' }
            : { type: data.type, iteration: data.iteration };
      }
      break;
    }

    case 'reasoning_delta': {
      const data = event.data as { text: string };
      msg.reasoningText = (msg.reasoningText || '') + data.text;
      if (attached) {
        chat.currentStatus = { ...chat.currentStatus, type: 'thinking' };
      }
      break;
    }

    case 'text_delta': {
      const data = event.data as { text: string };
      msg.content += data.text;
      break;
    }

    case 'tool_start': {
      const data = event.data as {
        name: string;
        tool_call_id?: string;
        item_count?: number;
      };
      mergeToolCall(msg, data);
      if (attached) {
        chat.currentStatus = { type: 'tool' };
      }
      break;
    }

    case 'tool_end': {
      const data = event.data as {
        name: string;
        tool_call_id?: string;
        status: ToolCallState['status'];
        item_count?: number;
        error?: string;
      };
      mergeToolCall(msg, data);
      // A held call (WI #5768) has not run: it waits for the user.
      if (data.status !== 'held') {
        if (!msg.toolsUsed) {
          msg.toolsUsed = [];
        }
        msg.toolsUsed.push({
          name: data.name,
          status: data.status === 'error' ? 'error' : 'success',
        });
      }
      if (attached) {
        chat.currentStatus = { type: 'processing' };
      }
      break;
    }

    case 'step': {
      const step = event.data as AiStep;
      // Each entity the answer saves shows on the page at once (WI #5767);
      // a step the reattach snapshot already held was shown before.
      if (addStep(msg, step)) {
        useAiLiveStore().stepSaved(chat.mode, chat.contextId, step);
      }
      break;
    }

    // Slice A of WI #5386 — orchestrator dispatched a subagent. Push a
    // placeholder record with status='running' so the chat UI can
    // render it immediately; later events fill in the result.
    case 'subagent_dispatched': {
      const data = event.data as {
        index: number;
        name: string;
        label: string;
        prompt: string;
        iteration: number;
      };
      if (!msg.subagentRuns) {
        msg.subagentRuns = [];
      }
      msg.subagentRuns.push({
        index: data.index,
        name: data.name,
        label: data.label,
        prompt: data.prompt,
        iteration: data.iteration,
        resultText: '',
        iterations: 0,
        toolsUsed: [],
        status: 'running',
      });
      if (attached) {
        chat.currentStatus = {
          ...chat.currentStatus,
          type: 'subagent',
          subagentRunning: msg.subagentRuns.filter(
            (r) => r.status === 'running',
          ).length,
          subagentTotal: msg.subagentRuns.length,
          subagentLabel: data.label,
        };
      }
      break;
    }

    // Progress events from inside a subagent's loop: the running tool
    // count + iteration on the in-flight record.
    case 'subagent_progress': {
      const data = event.data as {
        index: number;
        name: string;
        event: {
          kind: string;
          iteration?: number;
          tool?: string;
          status?: 'success' | 'error';
        };
      };
      const record = msg.subagentRuns?.find((r) => r.index === data.index);
      if (!record) {
        break;
      }
      if (
        (data.event.kind === 'iteration' || data.event.kind === 'tool_start') &&
        typeof data.event.iteration === 'number'
      ) {
        record.iterations = data.event.iteration;
      }
      if (
        data.event.kind === 'tool_end' &&
        data.event.tool &&
        data.event.status
      ) {
        record.toolsUsed.push({
          name: data.event.tool,
          iteration: data.event.iteration ?? record.iterations,
          status: data.event.status,
        });
      }
      break;
    }

    // Subagent finished — fill in the final result and flip status.
    case 'subagent_complete': {
      const data = event.data as {
        index: number;
        name: string;
        status: 'success' | 'error' | 'truncated' | 'stopped';
        iterations: number;
        tools_used: number;
        cost: number;
        result_text: string;
        error?: string;
      };
      if (!msg.subagentRuns) {
        msg.subagentRuns = [];
      }
      const record = msg.subagentRuns.find((r) => r.index === data.index);
      if (record) {
        record.status = data.status;
        record.iterations = data.iterations;
        record.resultText = data.result_text;
        record.cost = data.cost;
        record.error = data.error;
      }
      if (attached) {
        const running = msg.subagentRuns.filter(
          (r) => r.status === 'running',
        ).length;
        // Back to 'processing' once the last subagent of the batch ended.
        chat.currentStatus =
          running > 0
            ? {
                ...chat.currentStatus,
                type: 'subagent',
                subagentRunning: running,
                subagentTotal: msg.subagentRuns.length,
              }
            : { type: 'processing' };
      }
      break;
    }

    case 'code_input_delta': {
      const data = event.data as { code: string };
      if (!msg.codeBlocks) {
        msg.codeBlocks = [];
      }
      // Get current block, or create new one if previous already has output
      let currentBlock = msg.codeBlocks[msg.codeBlocks.length - 1];
      if (
        !currentBlock ||
        currentBlock.logs !== undefined ||
        (currentBlock.images && currentBlock.images.length > 0)
      ) {
        currentBlock = { code: '' };
        msg.codeBlocks.push(currentBlock);
      }
      currentBlock.code += data.code;
      break;
    }

    case 'code_output': {
      const data = event.data as { logs: string };
      if (msg.codeBlocks && msg.codeBlocks.length > 0) {
        const block = msg.codeBlocks[msg.codeBlocks.length - 1];
        block.logs = (block.logs || '') + data.logs;
        block.isExecuting = false;
      }
      break;
    }

    case 'file_output': {
      const data = event.data as {
        type: string;
        data: string;
        mime_type: string;
        attachment_id?: string;
      };
      if (
        data.type === 'image' &&
        msg.codeBlocks &&
        msg.codeBlocks.length > 0
      ) {
        const block = msg.codeBlocks[msg.codeBlocks.length - 1];
        if (!block.images) {
          block.images = [];
        }
        block.images.push({
          data: data.data,
          mimeType: data.mime_type,
          attachmentId: data.attachment_id,
        });
        block.isExecuting = false;
      }
      break;
    }

    case 'file_output_ref': {
      // Reference-only file event from the tail endpoint — no base64
      // payload since resumes can't replay the original stream. Adding
      // to msg.attachments lets the existing history-image rendering
      // path (AIChatMessage.fetchAttachmentImage) resolve it through
      // the attachments proxy.
      const data = event.data as {
        type: string;
        attachment_id: string;
        mime_type: string;
      };
      if (data.type === 'image' && data.attachment_id) {
        if (!msg.attachments) {
          msg.attachments = [];
        }
        if (!msg.attachments.some((a) => a.id === data.attachment_id)) {
          msg.attachments.push({
            id: data.attachment_id,
            type: data.type,
            mimeType: data.mime_type,
          });
        }
      }
      break;
    }

    case 'done':
    case 'stopped': {
      const data = event.data as {
        metadata?: Record<string, unknown> | null;
        estimated_cost?: number;
      };
      // Final authoritative metadata: the steps and calls, tokens and
      // subagent runs as the server tallied them.
      msg.isStreaming = false;
      msg.status = event.event === 'done' ? 'complete' : 'stopped';
      // An answer stopped before its first write has no metadata; keep
      // what the stream built.
      if (data.metadata) {
        applyMetadata(msg, data.metadata);
      }
      msg.estimatedCost = data.estimated_cost;
      if (attached) {
        chat.currentStatus = { type: 'idle' };
        chat.stopping = false;
      }
      break;
    }

    case 'permission_request': {
      // The answer waits for the user to allow changes (WI #5768): its write
      // calls are held, the card shows them; the decision goes on with it.
      const data = event.data as {
        metadata?: Record<string, unknown> | null;
        estimated_cost?: number;
      };
      msg.isStreaming = false;
      msg.status = 'awaiting_permission';
      if (data.metadata) {
        applyMetadata(msg, data.metadata);
      }
      msg.estimatedCost = data.estimated_cost;
      if (attached) {
        chat.currentStatus = { type: 'idle' };
        chat.stopping = false;
        markDecision(chat, true);
      }
      break;
    }

    case 'error': {
      // The answer shows the error inline, in German (WI #5769).
      const data = event.data as { message: string };
      msg.isStreaming = false;
      msg.status = 'error';
      msg.errorMessage = data.message;
      if (attached) {
        chat.currentStatus = { type: 'idle' };
        chat.stopping = false;
      }
      break;
    }
  }
}

/** Takes a run out of the background runs, and out of the chat's count. */
function dropBackgroundRun(chat: AiChat, run: Run) {
  if (backgroundRuns.delete(run)) {
    chat.backgroundRunCount--;
  }
}

/**
 * Ends a run; the chat is idle once no run is attached to it, and the AI
 * leaves the page once none of its runs is left (WI #5767).
 */
function endRun(chat: AiChat, run: Run) {
  dropBackgroundRun(chat, run);
  if (attachedRuns.get(run.key) === run) {
    attachedRuns.delete(run.key);
    markRunning(chat, false);
    chat.loading = false;
    chat.stopping = false;
    chat.currentStatus = { type: 'idle' };
    chat.streamStartTime = null;
  }
  if (!chat.loading && chat.backgroundRunCount === 0) {
    useAiLiveStore().runEnded(chat.mode, chat.contextId);
  }
}

/** Sends the stop of a named run to the server. */
async function requestStop(chat: AiChat, run: Run): Promise<void> {
  const messageId = run.message.id;
  const conversationId = run.conversationId;
  if (!messageId || !conversationId) {
    return;
  }
  try {
    if (chat.mode === 'template') {
      await TemplatesService.cancelAiTemplateMessage(
        chat.contextId,
        conversationId,
        messageId,
      );
    } else {
      await ProjectsService.cancelAiMessage(
        chat.contextId,
        conversationId,
        messageId,
      );
    }
  } catch (err) {
    // 409: the answer ended meanwhile — its stream says how.
    if (err instanceof ApiError && err.status === 409) {
      return;
    }
    console.error('Failed to stop the AI answer:', err);
    // The button turns back into Stop, so the user can try again.
    run.stopRequested = false;
    if (attachedRuns.get(run.key) === run) {
      chat.stopping = false;
    }
  }
}

/**
 * Detach the shown conversation from its running answer without
 * stopping it: the user switched to another conversation or started a
 * new one. The answer keeps running on the server and keeps its stream,
 * so its changes still reach the page when it ends; switching back
 * reattaches through the tail endpoint.
 */
function detachRun(chat: AiChat) {
  const key = chatKey(chat.mode, chat.contextId);
  const run = attachedRuns.get(key);
  if (run) {
    attachedRuns.delete(key);
    // Also one the user stopped, or one the server has not named yet: a
    // disconnect would not stop it (#5455), and only its stream refreshes
    // the page when it ends.
    backgroundRuns.add(run);
    chat.backgroundRunCount++;
  }
  // A page loaded from now on shows the other conversation.
  markRunning(chat, false);
  chat.loading = false;
  chat.stopping = false;
  chat.currentStatus = { type: 'idle' };
  chat.streamStartTime = null;
}

export const useAiAssistantStore = defineStore('aiAssistantStore', {
  state: (): AiAssistantStoreState => ({
    chats: {},
  }),

  getters: {
    chat:
      (state) =>
      (mode: AIAssistantMode, contextId: string): AiChat | undefined =>
        state.chats[chatKey(mode, contextId)],

    /**
     * An answer of this project/template is running (the Sparkles badge),
     * also one the user left for another conversation.
     */
    isRunning:
      (state) =>
      (mode: AIAssistantMode, contextId: string): boolean => {
        const chat = state.chats[chatKey(mode, contextId)];

        return !!chat && (chat.loading || chat.backgroundRunCount > 0);
      },

    /**
     * An answer of this project/template waits for the user to allow
     * changes (WI #5768) — the Sparkles button's badge.
     */
    isAwaitingDecision:
      (state) =>
      (mode: AIAssistantMode, contextId: string): boolean => {
        const chat = state.chats[chatKey(mode, contextId)];

        return !!chat && !chat.loading && !!waitingAnswer(chat);
      },
  },

  actions: {
    /**
     * The chat of a project/template. On first use it loads the
     * conversation the user had open there and rejoins its answer if that
     * is still running.
     */
    ensureChat(mode: AIAssistantMode, contextId: string): AiChat {
      const key = chatKey(mode, contextId);
      if (!this.chats[key]) {
        this.chats[key] = newChat(mode, contextId);
      }
      const chat = this.chats[key];
      if (!chat.initialized) {
        chat.initialized = true;
        forgetToolSelection();
        void this.loadAccess(mode, contextId);
        let saved: string | null = null;
        try {
          saved = localStorage.getItem(conversationStorageKey(chat));
        } catch {
          // Storage blocked: start empty.
        }
        if (saved) {
          chat.conversationId = saved;
          // Fire and forget: the chat shows its loading state meanwhile.
          void this.loadConversationMessages(mode, contextId, saved).catch(
            () => undefined,
          );
        } else {
          // No conversation, no answer to rejoin or to decide on.
          markRunning(chat, false);
          markDecision(chat, false);
        }
      }

      return chat;
    },

    /**
     * Rejoins an answer that was running here when the page was loaded (a
     * reload, another tab), also with the dock closed — for the running
     * indicator and the refresh at its end. Loads nothing without the mark.
     */
    rejoinRunningAnswer(mode: AIAssistantMode, contextId: string) {
      if (
        this.chats[chatKey(mode, contextId)]?.initialized ||
        !(markedRunning(mode, contextId) || markedDecision(mode, contextId))
      ) {
        return;
      }
      this.ensureChat(mode, contextId);
    },

    /** Whether the assistant offers its write tools to the user here. */
    async loadAccess(mode: AIAssistantMode, contextId: string): Promise<void> {
      const chat = this.ensureChat(mode, contextId);
      try {
        const access =
          mode === 'template'
            ? await TemplatesService.getAiTemplateAssistantAccess(contextId)
            : await ProjectsService.getAiAssistantAccess(contextId);
        chat.canEdit = access.can_edit;
      } catch (err) {
        // Unknown: the input shows neither "Nur lesen" nor the chip.
        console.error('Failed to load the AI assistant access:', err);
      }
    },

    /**
     * The input took the prompt of the failed message back (WI #5769): a
     * panel mounting again leaves the input as the user left it.
     */
    failedPromptRestored(mode: AIAssistantMode, contextId: string) {
      const failure = this.chat(mode, contextId)?.failure;
      if (failure) {
        failure.restored = true;
      }
    },

    /** The `+` menu's switches (WI #5768). */
    setToolSwitch(
      mode: AIAssistantMode,
      contextId: string,
      tool: 'webSearch' | 'codeInterpreter',
      on: boolean,
    ) {
      this.ensureChat(mode, contextId)[tool] = on;
    },

    async loadConversationMessages(
      mode: AIAssistantMode,
      contextId: string,
      convId: string,
    ): Promise<void> {
      const chat = this.ensureChat(mode, contextId);
      if (!convId) {
        return;
      }

      chat.loadingMessages = true;
      chat.loadError = null;

      try {
        // Fetch messages and conversation metadata in parallel. Both project
        // and template conversations expose a sticky-skill state, hydrated
        // from `active_skill_id` on the conversation details.
        const [response, conversation] = await Promise.all([
          mode === 'template'
            ? TemplatesService.getAiTemplateConversationMessages(
                contextId,
                convId,
                MESSAGE_LOAD_LIMIT,
                0,
                'asc',
              )
            : ProjectsService.getAiConversationMessages(
                contextId,
                convId,
                MESSAGE_LOAD_LIMIT,
                0,
                'asc', // Oldest first
              ),
          mode === 'template'
            ? TemplatesService.getAiTemplateConversation(
                contextId,
                convId,
              ).catch(() => null)
            : ProjectsService.getAiConversation(contextId, convId).catch(
                () => null,
              ),
        ]);

        chat.activeSkillId = conversation?.active_skill_id ?? null;
        chat.selectedAiModelId = conversation?.ai_model_id ?? null;
        chat.writePermission = conversation?.write_permission ?? false;

        // Handle empty message arrays
        chat.messages = (response.messages || []).map(convertToInternalMessage);

        // If the last assistant message is still streaming server-side,
        // rejoin it via the tail endpoint so the user sees the response
        // complete (or error out) without having to start over.
        const last = chat.messages[chat.messages.length - 1];
        if (
          last &&
          last.role === 'assistant' &&
          last.status === 'streaming' &&
          last.id
        ) {
          void this.attachToMessageStream(mode, contextId, convId, last);
        } else if (!attachedRuns.has(chatKey(mode, contextId))) {
          // The mark of a page closed while its answer ran.
          markRunning(chat, false);
        }
        markDecision(chat, !!waitingAnswer(chat));
      } catch (err) {
        console.error('Failed to load conversation messages:', err);

        if (err instanceof ApiError && err.status === 404) {
          // The chat is gone (deleted elsewhere): the panel starts a new one.
          resetConversation(chat);
        } else {
          // Said inline, in German (WI #5769).
          chat.loadError = aiErrorKind(
            err instanceof ApiError
              ? err.body?.message || err.message
              : err instanceof Error
                ? err.message
                : '',
            err instanceof ApiError ? err.status : undefined,
          );
        }
        throw err;
      } finally {
        chat.loadingMessages = false;
      }
    },

    /**
     * Send a message with streaming response. Shows live typing, reasoning
     * and the steps of the tools the answer runs. True when the server took
     * the message; one it did not take leaves the chat, which says why and
     * keeps the prompt for the input (WI #5769). `draft` is the input's
     * document, kept with the prompt.
     */
    async sendMessage(
      mode: AIAssistantMode,
      contextId: string,
      userMessage: string,
      mentionedItems?: Array<{
        id: string;
        title: string;
        type: 'activity' | 'phase' | 'workstream' | 'milestone';
      }>,
      displayHtml?: string,
      attachments?: AiChatAttachmentInput[],
      skillId?: string | null,
      aiModelId?: string | null,
      draft?: unknown,
    ): Promise<boolean> {
      if (!contextId) {
        throw new Error('Context ID is required');
      }
      if (!userMessage.trim()) {
        throw new Error('Message cannot be empty');
      }
      const chat = this.ensureChat(mode, contextId);
      const key = chatKey(mode, contextId);

      // A message instead of a decision (WI #5768): the backend answers the
      // held calls as rejected and the waiting answer ends.
      const waiting = waitingAnswer(chat);
      if (waiting) {
        settleWriteRequest(waiting, 'discarded');
        waiting.status = 'complete';
        markDecision(chat, false);
      }

      chat.loading = true;
      chat.stopping = false;
      chat.failure = null;
      chat.loadError = null;
      chat.currentStatus = { type: 'idle' };
      chat.streamStartTime = Date.now();

      // Add user message to conversation (optimistic — includes attachment
      // metadata so the chip row renders immediately in the bubble without
      // waiting for the backend to echo back)
      chat.messages.push({
        role: 'user',
        content: userMessage,
        displayHtml:
          displayHtml && displayHtml.length > 0 ? displayHtml : undefined,
        attachments:
          attachments && attachments.length > 0
            ? attachments.map((a) => ({
                id: a.id,
                type: a.type,
                mimeType: a.mime_type,
                source: 'input',
                filename: a.filename,
                size: a.size,
                // Carry slot_id locally so the *next* turn's slot panel
                // already knows this slot is filled — without waiting for
                // a conversation reload from the server.
                slotId: a.slot_id ?? undefined,
              }))
            : undefined,
      });

      // Placeholder for the assistant message; the stream writes into the
      // reactive copy the array holds.
      chat.messages.push({
        role: 'assistant',
        content: '',
        isStreaming: true,
        reasoningText: '',
      });
      const msg = chat.messages[chat.messages.length - 1];

      const startTime = performance.now();
      const run: Run = {
        key,
        abort: new AbortController(),
        message: msg,
        stopRequested: false,
      };
      attachedRuns.set(key, run);
      useAiLiveStore().runStarted(mode, contextId);

      try {
        const token = await authToken();

        const request: AIChatRequest | AiTemplateChatRequest = {
          message: userMessage,
          conversation_id: chat.conversationId || undefined,
          language: getLocale(),
          // The other tools are the backend's choice (WI #5768). The
          // meeting tools only for a user this tenant's transcript gate lets
          // in: it narrows the server's own gate until that has the same
          // allowlist.
          web_search: chat.webSearch || undefined,
          code_interpreter: chat.codeInterpreter || undefined,
          meeting_tools:
            mode === 'project' &&
            !useFeatureFlagStore().canAccessMeetingTranscripts
              ? false
              : undefined,
          mentioned_items:
            mentionedItems && mentionedItems.length > 0
              ? mentionedItems
              : undefined,
          attachments:
            attachments && attachments.length > 0 ? attachments : undefined,
          display_html:
            displayHtml && displayHtml.length > 0 ? displayHtml : undefined,
          // Both project and template chat consume `skill_id` — the backend
          // sets it as the conversation's sticky skill and replaces the
          // default tools with the skill's per-context allow-list for this
          // turn (and subsequent turns, until cleared).
          skill_id: skillId || undefined,
          // `ai_model.id` (UUID) of the row picked in the chat-input
          // selector. Backend validates against the realm's available list;
          // valid values get stamped as the conversation's sticky id.
          ai_model_id: aiModelId || undefined,
        };

        // Optimistic update: when a chip carries a skill id, the backend
        // will stamp it as the conversation's sticky skill.
        if (skillId) {
          chat.activeSkillId = skillId;
        }
        // Same optimistic update for model id — assume the backend will
        // accept the requested id. On rejection the next conversation fetch
        // will reconcile.
        if (aiModelId) {
          chat.selectedAiModelId = aiModelId;
        }

        const streamPath =
          mode === 'template'
            ? `/v1/templates/${contextId}/ai/chat/stream`
            : `/v1/projects/${contextId}/ai/chat/stream`;

        const response = await fetch(`${OpenAPI.BASE}${streamPath}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(request),
          signal: run.abort.signal,
          credentials: OpenAPI.WITH_CREDENTIALS ? 'include' : 'same-origin',
        });

        if (!response.ok) {
          throw new AiRequestError(response.status, await response.text());
        }

        if (!response.body) {
          throw new AiRequestError(response.status, 'No response body');
        }

        await readStream(chat, run, response.body);

        msg.isStreaming = false;
        if (msg.durationMs === undefined) {
          msg.durationMs = Math.round(performance.now() - startTime);
        }
        const attached = attachedRuns.get(key) === run;
        if (!run.sessionStarted) {
          // Refused before its answer started (e.g. another answer still
          // runs): nothing of it is stored.
          if (attached) {
            dropUnsent(chat, msg, {
              kind: aiErrorKind(msg.errorMessage),
              unsent: true,
              prompt: userMessage,
              draft,
              attachments,
              restored: false,
            });
            // A decision runs the answer this message would have ended (WI
            // #5768): show the chat as the server has it.
            if (waiting && chat.conversationId) {
              void this.loadConversationMessages(
                mode,
                contextId,
                chat.conversationId,
              ).catch(() => undefined);
            }
          }

          return false;
        }
        if (msg.status === 'error' && attached && !changedData(chat, msg)) {
          // Failed before it changed anything: it may simply go again.
          chat.failure = {
            kind: aiErrorKind(msg.errorMessage),
            unsent: false,
            prompt: userMessage,
            draft,
            attachments,
            restored: false,
          };
        }
        void refreshAfterRun(chat, msg);

        return true;
      } catch (err) {
        // Don't report abort errors: the user left this conversation, and
        // a run they left keeps going on the server.
        if (err instanceof DOMException && err.name === 'AbortError') {
          return !!run.sessionStarted;
        }

        const attached = attachedRuns.get(key) === run;
        if (msg.id && run.conversationId) {
          if (attached) {
            // The connection dropped after the answer started. The answer
            // keeps running on the server (#5455): rejoin it.
            void this.attachToMessageStream(
              mode,
              contextId,
              run.conversationId,
              msg,
            );
          } else {
            // A run the user left: show what it changed so far.
            msg.isStreaming = false;
            void refreshAfterRun(chat, msg);
          }

          return true;
        }
        if (!attached) {
          // It failed before its answer started, in a chat the user left.
          msg.isStreaming = false;

          return false;
        }
        console.error('Failed to send the message to the AI assistant:', err);
        // Said inline, in German, instead of the server's or the browser's
        // own text (WI #5769).
        dropUnsent(chat, msg, {
          kind: aiErrorKind(
            err instanceof ApiError
              ? err.body?.message || err.message
              : err instanceof Error
                ? err.message
                : '',
            err instanceof AiRequestError || err instanceof ApiError
              ? err.status
              : undefined,
          ),
          unsent: true,
          prompt: userMessage,
          draft,
          attachments,
          restored: false,
        });
        // The answer that waited still waits on the server (WI #5768).
        if (waiting && chat.conversationId) {
          void this.loadConversationMessages(
            mode,
            contextId,
            chat.conversationId,
          ).catch(() => undefined);
        }

        return false;
      } finally {
        endRun(chat, run);
      }
    },

    /**
     * Reattach to an in-flight (or terminal) assistant message via the
     * server-sent-events tail endpoint; the message (loaded from history
     * with status='streaming') is updated in place as events arrive.
     */
    async attachToMessageStream(
      mode: AIAssistantMode,
      contextId: string,
      convId: string,
      msg: ChatMessage,
    ): Promise<void> {
      const chat = this.ensureChat(mode, contextId);
      const key = chatKey(mode, contextId);
      const messageId = msg.id as string;

      // This tail takes over from a run the user had left for this answer,
      // and from any other stream attached to the chat.
      for (const background of backgroundRuns) {
        if (background.message.id === messageId) {
          background.abort.abort();
          dropBackgroundRun(chat, background);
        }
      }
      attachedRuns.get(key)?.abort.abort();

      const run: Run = {
        key,
        abort: new AbortController(),
        message: msg,
        conversationId: convId,
        stopRequested: false,
      };
      attachedRuns.set(key, run);
      markRunning(chat, true);
      useAiLiveStore().runStarted(mode, contextId);

      // Mirror the live-send UX: flip the chat into its "busy" state so the
      // input swaps its send button for stop, and seed a non-idle status.
      chat.loading = true;
      chat.stopping = false;
      chat.currentStatus = { type: 'processing' };
      // Seed the stream-start wall-clock from the assistant message's
      // createdAt so the bottom-right timer reflects the true elapsed time.
      chat.streamStartTime = msg.createdAt
        ? new Date(msg.createdAt).getTime()
        : Date.now();

      const startTime = performance.now();

      try {
        const token = await authToken();

        const streamPath =
          mode === 'template'
            ? `/v1/templates/${contextId}/ai/conversations/${convId}/messages/${messageId}/stream`
            : `/v1/projects/${contextId}/ai/conversations/${convId}/messages/${messageId}/stream`;

        const response = await fetch(`${OpenAPI.BASE}${streamPath}`, {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: run.abort.signal,
          credentials: OpenAPI.WITH_CREDENTIALS ? 'include' : 'same-origin',
        });

        if (!response.ok) {
          throw new Error(`Tail request failed: ${response.status}`);
        }
        if (!response.body) {
          throw new Error('No response body on tail stream');
        }

        await readStream(chat, run, response.body);

        msg.isStreaming = false;
        if (msg.durationMs === undefined) {
          msg.durationMs = Math.round(performance.now() - startTime);
        }
        void refreshAfterRun(chat, msg);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return; // user navigated away or a new send started
        }
        console.error('Failed to attach to AI message stream:', err);
        // Leave msg.status alone so a retry (future reload) can try again;
        // the user still sees whatever partial content had been loaded.
        msg.isStreaming = false;
      } finally {
        endRun(chat, run);
      }
    },

    /**
     * Stop the running answer (WI #5766): the server finishes the entity
     * being saved, starts nothing new, and ends the stream with `stopped` —
     * the stream stays attached until then.
     */
    async stop(mode: AIAssistantMode, contextId: string): Promise<void> {
      const chat = this.chat(mode, contextId);
      const run = attachedRuns.get(chatKey(mode, contextId));
      if (!chat || !run || chat.stopping) {
        return;
      }
      chat.stopping = true;
      run.stopRequested = true;
      // Without a name yet, the `session` event sends the stop.
      await requestStop(chat, run);
    },

    startNewConversation(mode: AIAssistantMode, contextId: string) {
      const chat = this.ensureChat(mode, contextId);
      detachRun(chat);
      chat.failure = null;
      chat.loadError = null;
      resetConversation(chat);
    },

    async switchConversation(
      mode: AIAssistantMode,
      contextId: string,
      conversationId: string,
    ): Promise<void> {
      if (!contextId) {
        throw new Error('Context ID is required');
      }
      const chat = this.ensureChat(mode, contextId);
      detachRun(chat);
      // Nothing of the chat the user left stays — its skill, model and
      // permission neither — also when this one does not load.
      chat.failure = null;
      resetConversation(chat);
      setConversationId(chat, conversationId);
      try {
        await this.loadConversationMessages(mode, contextId, conversationId);
      } catch (err) {
        // Picked from the history, but deleted elsewhere meanwhile.
        if (err instanceof ApiError && err.status === 404) {
          chat.loadError = 'notFound';
        }
        throw err;
      }
    },

    /**
     * The user's decision on the write calls the waiting answer holds (WI
     * #5768): an allow runs exactly the held calls — "Für diesen Chat
     * erlauben" (`chat`) also allows every later change of the chat,
     * "Einmal erlauben" (`once`) only these; "Nein, nur lesen" answers them
     * as rejected. Either way the same answer goes on, streamed into its
     * message.
     */
    async decide(
      mode: AIAssistantMode,
      contextId: string,
      allow: boolean,
      scope: 'once' | 'chat' = 'once',
    ): Promise<void> {
      const chat = this.ensureChat(mode, contextId);
      const msg = waitingAnswer(chat);
      const conversationId = chat.conversationId;
      if (!msg?.id || !conversationId || chat.loading) {
        return;
      }
      const key = chatKey(mode, contextId);

      // The card collapses at once; the stream says how the answer goes on.
      if (allow) {
        settleWriteRequest(msg, 'allowed', scope);
      } else {
        settleWriteRequest(msg, 'rejected');
      }
      if (allow && scope === 'chat') {
        chat.writePermission = true;
      }
      msg.status = 'streaming';
      msg.isStreaming = true;
      markDecision(chat, false);
      chat.loading = true;
      chat.stopping = false;
      chat.failure = null;
      chat.currentStatus = { type: 'processing' };
      chat.streamStartTime = Date.now();

      const run: Run = {
        key,
        abort: new AbortController(),
        message: msg,
        conversationId,
        stopRequested: false,
      };
      attachedRuns.set(key, run);
      markRunning(chat, true);
      useAiLiveStore().runStarted(mode, contextId);

      try {
        const token = await authToken();
        const decisionPath =
          mode === 'template'
            ? `/v1/templates/${contextId}/ai/conversations/${conversationId}/messages/${msg.id}/decision`
            : `/v1/projects/${contextId}/ai/conversations/${conversationId}/messages/${msg.id}/decision`;

        const response = await fetch(`${OpenAPI.BASE}${decisionPath}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ allow, scope }),
          signal: run.abort.signal,
          credentials: OpenAPI.WITH_CREDENTIALS ? 'include' : 'same-origin',
        });

        if (!response.ok || !response.body) {
          // Decided elsewhere meanwhile (409), or refused: show the answer
          // as the server has it.
          endRun(chat, run);
          await this.loadConversationMessages(mode, contextId, conversationId);

          return;
        }

        // The answer's metadata at its end says how long it took in all.
        await readStream(chat, run, response.body);

        msg.isStreaming = false;
        if (!run.sessionStarted) {
          // Refused: another decision or a new message came first. The
          // answer goes on (or ended) elsewhere — show it as the server has
          // it, without the refusal on the message.
          endRun(chat, run);
          await this.loadConversationMessages(mode, contextId, conversationId);

          return;
        }
        void refreshAfterRun(chat, msg);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        if (attachedRuns.get(key) === run) {
          // The connection dropped: the answer goes on on the server.
          void this.attachToMessageStream(mode, contextId, conversationId, msg);
        } else {
          msg.isStreaming = false;
          void refreshAfterRun(chat, msg);
        }
      } finally {
        endRun(chat, run);
      }
    },

    /**
     * Revokes the chat's write access (the chip "Darf ändern ×", WI #5768):
     * the next write call asks again — also one of the running answer.
     */
    async revokeWritePermission(
      mode: AIAssistantMode,
      contextId: string,
    ): Promise<void> {
      const chat = this.ensureChat(mode, contextId);
      if (!chat.conversationId) {
        chat.writePermission = false;

        return;
      }
      const updated =
        mode === 'template'
          ? await TemplatesService.updateAiTemplateConversation(
              contextId,
              chat.conversationId,
              { write_permission: false },
            )
          : await ProjectsService.updateAiConversation(
              contextId,
              chat.conversationId,
              { write_permission: false },
            );
      chat.writePermission = updated.write_permission ?? false;
    },

    /**
     * Clear the conversation-level sticky skill so the next user turn runs
     * without any skill applied. Server-side this is a `PATCH` on the
     * conversation; we update local state on success.
     */
    async clearActiveSkill(
      mode: AIAssistantMode,
      contextId: string,
    ): Promise<void> {
      const chat = this.ensureChat(mode, contextId);
      if (!contextId || !chat.conversationId) {
        chat.activeSkillId = null;

        return;
      }
      try {
        const updated =
          mode === 'template'
            ? await TemplatesService.updateAiTemplateConversation(
                contextId,
                chat.conversationId,
                { active_skill_id: null },
              )
            : await ProjectsService.updateAiConversation(
                contextId,
                chat.conversationId,
                { active_skill_id: null },
              );
        chat.activeSkillId = updated.active_skill_id ?? null;
      } catch (err) {
        console.error('Failed to clear active skill:', err);
        throw err;
      }
    },
  },
});
